function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useRef, useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import { Icon } from '../Icon';
import { Popup, bindSupportingElement, usePopupState, UsePopupStateTriggers } from '../Popup';
import { createSyntheticEvent } from '../../_internal_/utils/createSyntheticEvent';
import { useForkRef } from '../../hooks/useForkRef';
import { Menu } from '../Menu';
import { Tags } from '../Tags';
import { Tag } from '../Tag';
import { FieldText } from '../FieldText';
import { useListboxState, IUseListboxState, StateListbox } from '../../hooks/useListboxState';
import { useUniqId } from '../../hooks/useUniqId';
import { CollectionOption } from '../../hooks/useListboxState';
require("./Select.css");
var styles = {
  "Root": "Select-module-Root_3ZM",
  "FieldText": "Select-module-FieldText_2bd",
  "hasHiddenIndicator": "Select-module-hasHiddenIndicator_1A2",
  "expanded": "Select-module-expanded_1aO",
  "focused": "Select-module-focused_29r",
  "Popup": "Select-module-Popup_3rc",
  "NoOptions": "Select-module-NoOptions_V5r",
  "Loading": "Select-module-Loading_nhi",
  "IconExpandIndicator": "Select-module-IconExpandIndicator_2xV",
  "Tags": "Select-module-Tags_2kR",
  "hasError": "Select-module-hasError_1Kk"
};
export const cnSelect = cn('Select');
const emptyArray = [];

/**
 * Компонент для демонстрации списка подсказок, при заполнении текстового поля
 * @interface ISelectProps
 * @author s.ivannikova
 */
export const Select = ({
  disableFilter = false,
  checkedOptions,
  className,
  controlRef,
  filterOptions,
  innerRef,
  isMultiselectable = false,
  label,
  loading,
  name,
  noOptionsText = 'По запросу ничего не найдено',
  hasClear = false,
  hasError,
  hasExpandIndicator = true,
  onBlur,
  onChange,
  onCheck,
  onCollapse,
  onExpand,
  onFocus,
  options = emptyArray,
  style = {},
  value = '',
  hasIndependentLabel = false,
  ...rest
}) => {
  const [allowFilter, setAllowFilter] = useState(!disableFilter);
  const {
    checkedEvent,
    checkedKeys,
    collectionOptions,
    interactiveKeys,
    onKeyDown,
    removeCheckedKey,
    selectedKey,
    ...listBoxState
  } = useListboxState({
    allowFilter,
    checkedOptions,
    filterOptions,
    isMultiselectable,
    options,
    query: value
  });
  const popupState = usePopupState({
    triggers: [UsePopupStateTriggers.focus]
  });
  const [focused, setFocused] = useState(false);
  const popupId = useRef(`popup-${Math.random().toString(16).slice(-4)}`);
  const popupRef = useRef();
  const id = useUniqId('menu');
  const inputId = useUniqId('input');
  const _controlRef = useRef();
  const _innerRef = useRef();
  const ownRef = useForkRef(_controlRef, controlRef);
  const isExpanded = focused && options.length > 0 || Boolean(loading);
  useEffect(() => {
    const handleCollapse = () => {
      onCollapse?.(popupId.current);
    };
    const handleExpand = e => {
      if (e.target?.getAttribute('data-popup-value')?.includes(popupId.current)) {
        onExpand?.(popupId.current);
      }
    };
    if (typeof window === 'undefined') {
      return;
    }
    document?.addEventListener('eapteka-ui:popupclose', handleCollapse);
    document?.addEventListener('eapteka-ui:popupopen', handleExpand);
    return () => {
      document?.removeEventListener('eapteka-ui:popupclose', handleCollapse);
      document?.removeEventListener('eapteka-ui:popupopen', handleExpand);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupState.visibleId]);
  const setInputValue = useCallback((key, eventType) => {
    const input = _controlRef.current;
    if (!input || !onChange) return;
    input.value = key && collectionOptions?.get(key) && eventType !== 'remove' ? collectionOptions?.get(key)?.label || '' : '';
    const event = new Event('change', {
      bubbles: true
    });
    input.dispatchEvent(event);
    const syntheticEvent = createSyntheticEvent(event);
    onChange(syntheticEvent);
  }, [collectionOptions, onChange]);
  useEffect(() => {
    // при управлении с клавиатуры временно подставляем в инпут значение,
    // но не вызываем событие, таким образом в родительском стейте продолжает храниться ранее отмеченное значение
    if (_controlRef.current) {
      _controlRef.current.value = selectedKey && collectionOptions.get(selectedKey)?.label || '';
    }
  }, [selectedKey, collectionOptions]);
  useEffect(() => {
    if (_controlRef.current?.value && isMultiselectable) {
      _controlRef.current.value = '';
    }
    if (!isMultiselectable) {
      setAllowFilter(false);
      setInputValue(checkedEvent.key, checkedEvent.type);
      window.requestAnimationFrame(() => {
        setAllowFilter(!disableFilter);
      });
    }
    if (onCheck) {
      onCheck(checkedEvent.key, collectionOptions?.get(checkedEvent.key), checkedEvent.type);
    }
    return () => {
      setAllowFilter(!disableFilter);
    };
  }, [checkedEvent, collectionOptions, onCheck, isMultiselectable, setInputValue, disableFilter]);
  const handleFocus = useCallback(e => {
    if (onFocus) {
      onFocus(e);
    }
    popupState.onOpen(e);
    setFocused(true);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [onFocus]);
  const handleBlur = useCallback(e => {
    popupState.onClose(e);
    if (onBlur) {
      onBlur(e);
    }
    setFocused(false);
    if (!isMultiselectable) {
      setInputValue(checkedEvent.key, checkedEvent.type);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [checkedEvent.key, checkedEvent.type, isMultiselectable, onBlur, setInputValue]);
  const handleKeyDown = useCallback(e => {
    onKeyDown(e);
    if (e.key === 'Enter' && !isMultiselectable) {
      popupState.onClose(e);
    }
  }, [isMultiselectable, onKeyDown, popupState]);
  const handleRemove = useCallback((_e, id) => {
    removeCheckedKey(id);
  }, [removeCheckedKey]);
  const popupProps = {
    ...{
      ...bindSupportingElement(popupId.current, popupState),
      onFocus: handleFocus
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    style: style,
    className: cn(styles.Root, className, {
      [styles.hasIndependentLabel]: hasIndependentLabel,
      [styles.hasError]: hasError
    }),
    ref: innerRef
  }, /*#__PURE__*/React.createElement(FieldText, _extends({
    addonAfter: hasExpandIndicator ? /*#__PURE__*/React.createElement(Icon, {
      name: isExpanded ? 'chevronUp' : 'chevronDown',
      "aria-hidden": "true",
      size: "m",
      className: styles.IconExpandIndicator
    }) : null,
    "aria-controls": id,
    "aria-expanded": isExpanded,
    className: cn(styles.FieldText, {
      [styles.hasHiddenIndicator]: hasExpandIndicator || hasClear,
      [styles.expanded]: isExpanded,
      [styles.focused]: focused
    }),
    hasClear: hasClear,
    innerRef: _innerRef,
    controlRef: ownRef,
    id: inputId,
    name: name,
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
    onInput: onChange,
    role: "combobox",
    label: label,
    hasError: hasError,
    value: value,
    hasIndependentLabel: hasIndependentLabel,
    variant: "default"
  }, popupProps, rest)), isMultiselectable && checkedKeys.length > 0 && /*#__PURE__*/React.createElement(Tags, {
    className: styles.Tags
  }, checkedKeys.map(tag => /*#__PURE__*/React.createElement(Tag, {
    key: tag,
    id: tag,
    label: collectionOptions.get(tag)?.label,
    size: "m",
    theme: "default",
    onRemove: handleRemove
  }))), /*#__PURE__*/React.createElement(Popup, {
    className: cn(styles.Popup, {
      [styles.focused]: focused
    }),
    id: popupId.current,
    innerRef: popupRef,
    isVisible: isExpanded,
    placement: "bottom-start",
    target: _innerRef.current,
    variant: "default",
    style: {
      width: _innerRef.current?.offsetWidth
    }
  }, Boolean(interactiveKeys.length) && options.length > 0 && /*#__PURE__*/React.createElement(Menu, _extends({
    id: id,
    selectedKey: selectedKey,
    checkedKeys: checkedKeys,
    collectionOptions: collectionOptions
  }, listBoxState)), !interactiveKeys.length && options.length > 0 && allowFilter && /*#__PURE__*/React.createElement("div", {
    className: styles.NoOptions
  }, noOptionsText), loading && options.length === 0 && /*#__PURE__*/React.createElement("div", {
    className: styles.Loading
  }, "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430...")));
};
if (process.env.NODE_ENV !== 'production') {
  Select.displayName = 'Select';
}